import { Injectable } from '@angular/core';
import { BulkImportInvalidRecord, BulkImportStatus, BulkImportType } from '@dr-customer-offers-ui/lib-interfaces';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from './toast.service';

@Injectable({ providedIn: 'root' })
export class BulkImportNotificationService {
  private readonly i18nPrefix = 'COM.DIALOGS.BULK_UPLOAD';

  constructor(
    private toastService: ToastService,
    private translate: TranslateService
  ) {}

  private capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  private getCapitalizedTranslation(key: string, params?: object): string {
    return this.capitalize(this.translate.instant(key, params));
  }

  private getImportLabel(importType: BulkImportType | null): string {
    return this.translate
      .instant(importType === BulkImportType.Baselines ? `${this.i18nPrefix}.IMPORT_TYPE.BASELINES` : `${this.i18nPrefix}.IMPORT_TYPE.OFFERS`)
      .toLowerCase();
  }

  showStatusToast(status: BulkImportStatus, importType: BulkImportType | null, rowsInvalid?: number): void {
    const importLabel = this.getImportLabel(importType);

    switch (status) {
      case BulkImportStatus.Created:
        this.toastService.showSuccess(
          this.getCapitalizedTranslation(`${this.i18nPrefix}.STATUS.IMPORT_CREATED_TITLE`, { importLabel }),
          this.translate.instant(`${this.i18nPrefix}.STATUS.IMPORT_CREATED_MSG`)
        );
        break;
      case BulkImportStatus.Processing:
        this.toastService.showSuccess(
          this.getCapitalizedTranslation(`${this.i18nPrefix}.STATUS.IMPORT_IN_PROGRESS_TITLE`, { importLabel }),
          this.translate.instant(`${this.i18nPrefix}.STATUS.IMPORT_IN_PROGRESS_MSG`)
        );
        break;
      case BulkImportStatus.Succeeded:
        this.toastService.showSuccess(
          this.getCapitalizedTranslation(`${this.i18nPrefix}.STATUS.IMPORT_SUCCESS_TITLE`, { importLabel }),
          this.translate.instant(`${this.i18nPrefix}.STATUS.IMPORT_SUCCESS_MSG`),
          0
        );
        break;
      case BulkImportStatus.Partial:
        this.toastService.showWarning(
          this.getCapitalizedTranslation(`${this.i18nPrefix}.STATUS.IMPORT_PARTIAL_TITLE`, { importLabel }),
          this.translate.instant(`${this.i18nPrefix}.STATUS.IMPORT_PARTIAL_MSG`, { rowsInvalid, importLabel }),
          0
        );
        break;

      case BulkImportStatus.Failed:
      case BulkImportStatus.Cancelled:
        this.toastService.showError(
          this.getCapitalizedTranslation(`${this.i18nPrefix}.STATUS.IMPORT_FAILED_TITLE`, { importLabel }),
          this.translate.instant(`${this.i18nPrefix}.STATUS.IMPORT_FAILED_MSG`),
          0
        );
        break;
    }
  }

  showErrorToast(error: string, importType: BulkImportType | null): void {
    const importLabel = this.getImportLabel(importType);
    this.toastService.showError(
      this.getCapitalizedTranslation(`${this.i18nPrefix}.STATUS.IMPORT_FAILED_TITLE`, { importLabel }),
      this.translate.instant(error),
      0
    );
  }

  showInvalidRecordsToast(errors: BulkImportInvalidRecord[]): void {
    errors.forEach((e) => {
      const title = this.translate.instant(`${this.i18nPrefix}.STATUS.IMPORT_INVALID_ROW_TITLE`, {
        row: e.row_number,
        type: e.error_type,
        code: e.error_code
      });
      const detailsLabel = this.translate.instant(`${this.i18nPrefix}.STATUS.IMPORT_INVALID_ROW_DETAILS`);
      const content = `
        <div>
          <strong>${title}</strong>
          <div><strong>${detailsLabel}</strong> ${e.message}</div>
        </div>
      `;
      this.toastService.showError(this.translate.instant(`${this.i18nPrefix}.STATUS.IMPORT_INVALID_ROW_TOAST_TITLE`), content, 10000, true);
    });
  }
}
